import apiClient from "./apiClient";
import { ENDPOINTS } from "./config";

// Types based on the API documentation
export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  nationality?: string;
  countryOfResidence?: string;
  dateOfBirth?: string;
  phone?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  nationality?: string;
  countryOfResidence?: string;
  phone?: string;
  address?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ResendVerificationRequest {
  email: string;
}

export interface DeleteAccountRequest {
  password: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  nationality?: string;
  countryOfResidence?: string;
  dateOfBirth?: string;
  phone?: string;
  address?: string;
  role: "STUDENT" | "ADMIN";
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: User;
    token: string;
  };
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  data: User;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  errors?: string[];
}

/**
 * AuthService class handles all authentication-related API calls
 */
class AuthService {
  private tokenKey = "authToken";
  private userKey = "authUser";

  /**
   * Register a new user
   */
  public async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      ENDPOINTS.AUTH.REGISTER,
      userData
    );

    if (!response.success) {
      const details = response.error?.details;
      const backendErrors: string[] | undefined = details?.errors;
      const message =
        backendErrors && backendErrors.length > 0
          ? backendErrors.join(" ")
          : response.error?.message || "Registration failed";
      throw new Error(message);
    }

    if (response.data?.data) {
      this.setAuthData(response.data.data.token, response.data.data.user);
    }

    return response.data!;
  }

  /**
   * Login user
   */
  public async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      ENDPOINTS.AUTH.LOGIN,
      credentials
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Login failed");
    }

    if (response.data?.data) {
      this.setAuthData(response.data.data.token, response.data.data.user);
    }

    return response.data!;
  }

  /**
   * Logout user
   */
  public async logout(): Promise<ApiResponse> {
    try {
      const response = await apiClient.post<ApiResponse>(
        ENDPOINTS.AUTH.LOGOUT,
        {},
        true
      );
      this.clearAuthData();

      if (!response.success) {
        throw new Error(response.error?.message || "Logout failed");
      }

      return response.data!;
    } catch (error) {
      // Clear local data even if logout fails
      this.clearAuthData();
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  public async getProfile(): Promise<ProfileResponse> {
    const response = await apiClient.get<ProfileResponse>(
      ENDPOINTS.AUTH.ME,
      undefined,
      true
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to get profile");
    }

    return response.data!;
  }

  /**
   * Update user profile
   */
  public async updateProfile(
    userData: UpdateProfileRequest
  ): Promise<ProfileResponse> {
    const response = await apiClient.put<ProfileResponse>(
      ENDPOINTS.AUTH.ME, // Use same endpoint as get profile (/api/auth/profile) but with PUT method
      userData,
      true
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to update profile");
    }

    if (response.data?.data) {
      this.updateStoredUser(response.data.data);
    }

    return response.data!;
  }

  /**
   * Change password
   */
  public async changePassword(
    passwordData: ChangePasswordRequest
  ): Promise<ApiResponse> {
    // Remove confirmNewPassword from the payload - only send currentPassword and newPassword
    const payload: ChangePasswordPayload = {
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    };

    const response = await apiClient.post<ApiResponse>(
      ENDPOINTS.AUTH.CHANGE_PASSWORD, // Use auth route instead of user route
      payload,
      true
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to change password");
    }

    return response.data!;
  }

  /**
   * Request password reset
   */
  public async forgotPassword(
    data: ForgotPasswordRequest
  ): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>(
      ENDPOINTS.AUTH.FORGOT_PASSWORD,
      data
    );

    if (!response.success) {
      throw new Error(
        response.error?.message || "Failed to request password reset"
      );
    }

    return response.data!;
  }

  /**
   * Reset password with token
   */
  public async resetPassword(data: ResetPasswordRequest): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>(
      ENDPOINTS.AUTH.RESET_PASSWORD,
      data
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to reset password");
    }

    return response.data!;
  }

  /**
   * Verify email with token
   */
  public async verifyEmail(token: string): Promise<ApiResponse> {
    const response = await apiClient.get<ApiResponse>(
      ENDPOINTS.AUTH.VERIFY_EMAIL.replace(":token", token)
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to verify email");
    }

    return response.data!;
  }

  /**
   * Resend email verification
   */
  public async resendVerification(
    data: ResendVerificationRequest
  ): Promise<ApiResponse> {
    const response = await apiClient.post<ApiResponse>(
      ENDPOINTS.AUTH.VERIFY_EMAIL,
      data
    );

    if (!response.success) {
      throw new Error(
        response.error?.message || "Failed to resend verification"
      );
    }

    return response.data!;
  }

  /**
   * Refresh JWT token
   */
  public async refreshToken(): Promise<{
    success: boolean;
    data: { token: string };
  }> {
    const response = await apiClient.post<{
      success: boolean;
      data: { token: string };
    }>(ENDPOINTS.AUTH.REFRESH_TOKEN, {}, true);

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to refresh token");
    }

    if (response.data?.data) {
      this.setToken(response.data.data.token);
    }

    return response.data!;
  }

  /**
   * Delete user account
   */
  public async deleteAccount(data: DeleteAccountRequest): Promise<ApiResponse> {
    const response = await apiClient.request<ApiResponse>(
      ENDPOINTS.AUTH.DELETE,
      {
        method: "DELETE" as any,
        body: JSON.stringify(data),
        headers: {
          "Content-Type": "application/json",
        },
        withAuth: true,
      }
    );

    if (!response.success) {
      throw new Error(response.error?.message || "Failed to delete account");
    }

    if (response.data?.success) {
      this.clearAuthData();
    }

    return response.data!;
  }

  // Token and user management methods

  /**
   * Get stored authentication token
   */
  public getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  /**
   * Set authentication token
   */
  public setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  /**
   * Get stored user data
   */
  public getStoredUser(): User | null {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Set authentication data (token and user)
   */
  public setAuthData(token: string, user: User): void {
    localStorage.setItem(this.tokenKey, token);
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Update stored user data
   */
  public updateStoredUser(user: User): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  /**
   * Clear all authentication data
   */
  public clearAuthData(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  /**
   * Check if user's email is verified
   */
  public isEmailVerified(): boolean {
    const user = this.getStoredUser();
    return user?.isEmailVerified || false;
  }

  /**
   * Validate token format (basic validation)
   */
  public isValidTokenFormat(token: string): boolean {
    // Basic JWT format validation (header.payload.signature)
    const parts = token.split(".");
    return parts.length === 3;
  }

  /**
   * Get user role
   */
  public getUserRole(): string | null {
    const user = this.getStoredUser();
    return user?.role || null;
  }

  /**
   * Check if user has specific role
   */
  public hasRole(role: string): boolean {
    return this.getUserRole() === role;
  }

  /**
   * Check if token is expired (basic check based on JWT payload)
   */
  public isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }

  /**
   * Get token expiration time
   */
  public getTokenExpiration(): Date | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return new Date(payload.exp * 1000);
    } catch (error) {
      return null;
    }
  }
}

export const authService = new AuthService();
export default authService;
